/**
 * ARKO-Routing V2 (kein rohes CoT, keine PII/Aktenzitate).
 *
 * AI-Act-Systeminventar (Transparenz, Labor): Feldnamen/Labels in ArkoReasoningTraceCard
 * sind 1:1 übernehmbar — intent, path (Route), confidence, sources (norm_id), workflow_step.
 */

export interface ReasoningTraceSource {
  norm_id: string;
}

export interface ReasoningTraceStep {
  step: string;
  ok: boolean;
  detail?: string;
  ms?: number;
  chars?: number;
}

export interface ReasoningTrace {
  intent: string;
  path: string;
  not_triggered: string[];
  heuristic_reason?: string;
  mode?: string;
  confidence?: number;
  workflow_step?: string;
  alternatives?: string[];
  /** LEXCORE-Norm-IDs (ohne Volltext) — AI-Act-Quellenfeld. */
  sources?: ReasoningTraceSource[];
  /** Web-Artikel-Fetch-Metadaten (kein Volltext). */
  web_fetch?: {
    url: string;
    host: string;
    allowed: boolean;
    chars: number;
  };
  regel_id?: string;
  motor_audit_id?: string;
  execution_id?: string;
  request_id?: string;
  pipeline: ReasoningTraceStep[];
  warnings: string[];
  error: string | null;
}

const ROUTING_LABELS: Record<string, string> = {
  frist_vorschlag: 'Frist-Vorschlag',
  schreiben_erstellen: 'Schreiben erstellen',
  rechtsfrage: 'Rechtsfrage (LEXCORE)',
  judicature_recherche: 'OGH-Judikatur',
  vertrag_erstellen: 'Vertrag erstellen',
  allrounder: 'Allrounder',
  frist_heuristic_chat_doc: 'Frist-Heuristik (Chat + Dokument)',
  frist_heuristic_document: 'Frist-Heuristik (Dokument)',
  lexcore_rechtsfrage: 'LEXCORE Rechtsfrage',
  allrounder_fallback: 'Allrounder-Fallback',
  background_document_analysis: 'Hintergrundanalyse (Dokument)',
  document_question_override_summary: 'Dokument-Frage → Zusammenfassung/Meta',
  document_question_override_analyze: 'Dokument-Frage → Analyse',
  document_question_override_lexcore: 'Dokument-Frage → LEXCORE',
  chip_override_summary_meta: 'Chip-Override (Zusammenfassung/Meta)',
  override_summary_meta: 'Override Zusammenfassung/Meta',
  summary_or_meta: 'Zusammenfassung/Meta',
  section_or_law_match: '§-/Gesetzes-Match',
  keyword_match: 'Schlüsselwort-Match',
  topic_with_documentContext: 'Thema + Dokumentkontext',
  sachverhalt_analyse: 'Sachverhaltsanalyse (Subsumtion)',
  analyse_hub_lexcore: 'Hub Analysieren → LEXCORE',
  nachbar_immission_topic: 'Nachbar/Immission → LEXCORE',
  paragraph_wish_topic: '§§-Wunsch + Thema',
  contract_create: 'Vertrag erstellen',
  schreiben_create: 'Schreiben erstellen',
  document_welcome: 'Dokument-Begrüßung',
  frist_vorschlag_intent: 'Expliziter Frist-Intent',
  lexcore: 'LEXCORE',
  ollama: 'Ollama (Allrounder)',
  direct: 'Direktantwort (ohne LLM)',
  frist_motor: 'Verfahrensmotor (Frist)',
  lexcore_answer: 'LEXCORE-Antwort',
  judicature_answer: 'JUDICATURE-Antwort',
  judicature_premium: 'OGH-Recherche (Premium)',
  judicature_empty: 'OGH-Recherche ohne Treffer',
  judicature_unavailable: 'OGH-Recherche nicht verfügbar',
  premium_entitlement_gate: 'Premium-Entitlement',
  schreiben_draft: 'Schreiben-Entwurf',
  vertrag_generator: 'Vertragsgenerator',
  intent_detect: 'Intent-Erkennung',
  normalize: 'Normalisierung',
  document_context: 'Dokumentkontext',
  frist_probe: 'Frist-Sonde',
  llm: 'LLM-Aufruf',
};

const MODE_LABELS: Record<string, string> = {
  lexcore: 'LEXCORE',
  ollama: 'Ollama',
  direct: 'Direkt',
  judicature: 'JUDICATURE',
};

/** Doppelte Pfad-Keys (z. B. allrounder_allrounder) → kanonischer Intent. */
export function normalizeRoutingKey(key: string): string {
  const k = key.trim();
  if (!k) return k;
  const slash = k.indexOf('/');
  if (slash > 0) {
    const head = k.slice(0, slash);
    const tail = k.slice(slash + 1);
    if (head && head === tail) return head;
  }
  const parts = k.split('_').filter(Boolean);
  if (parts.length >= 2 && parts.every((p) => p === parts[0])) {
    return parts[0];
  }
  return k;
}

export function routingLabel(key: string): string {
  const k = normalizeRoutingKey(key.trim());
  if (!k) return '—';
  return ROUTING_LABELS[k] ?? k.replace(/_/g, ' ');
}

export function modeLabel(mode: string | undefined): string {
  const k = String(mode ?? '').trim().toLowerCase();
  if (!k) return '—';
  return MODE_LABELS[k] ?? routingLabel(k);
}

export function confidenceLabel(confidence: number | undefined): string {
  if (confidence == null || !Number.isFinite(confidence)) return '—';
  const pct = Math.round(Math.max(0, Math.min(1, confidence)) * 100);
  return `${pct} %`;
}

export function tracePathSummary(trace: ReasoningTrace): string {
  const parts = [
    routingLabel(trace.intent),
    routingLabel(trace.path),
  ].filter((part) => part !== '—');
  if (trace.confidence != null) parts.push(confidenceLabel(trace.confidence));
  return parts.join(' · ');
}

export function parseReasoningTrace(raw: unknown): ReasoningTrace | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const intent = String(o.intent ?? '').trim();
  const path = String(o.path ?? '').trim();
  if (!intent && !path) return null;
  const notRaw = o.not_triggered;
  const not_triggered = Array.isArray(notRaw)
    ? notRaw.map((x) => String(x).trim()).filter(Boolean)
    : [];
  const trace: ReasoningTrace = {
    intent: normalizeRoutingKey(intent || 'allrounder'),
    path: normalizeRoutingKey(path || intent || 'allrounder'),
    not_triggered,
    pipeline: [],
    warnings: [],
    error: null,
  };
  const heuristicReason = String(o.heuristic_reason ?? '').trim();
  if (heuristicReason) trace.heuristic_reason = heuristicReason;
  const mode = String(o.mode ?? '').trim();
  if (mode) trace.mode = mode;
  const confidence = Number(o.confidence);
  if (Number.isFinite(confidence) && confidence >= 0 && confidence <= 1) {
    trace.confidence = confidence;
  }
  const workflowStep = String(o.workflow_step ?? '').trim();
  if (workflowStep) trace.workflow_step = workflowStep;
  const altRaw = o.alternatives;
  const alternatives = Array.isArray(altRaw)
    ? altRaw.map((x) => String(x).trim()).filter(Boolean)
    : [];
  if (alternatives.length > 0) trace.alternatives = alternatives.map(normalizeRoutingKey);
  const sourcesRaw = o.sources;
  if (Array.isArray(sourcesRaw)) {
    const sources = sourcesRaw.flatMap((entry): ReasoningTraceSource[] => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
      const normId = String((entry as Record<string, unknown>).norm_id ?? '').trim();
      return normId ? [{ norm_id: normId }] : [];
    });
    if (sources.length > 0) trace.sources = sources;
  }
  const regel = String(o.regel_id ?? '').trim();
  if (regel) trace.regel_id = regel;
  const motor = String(o.motor_audit_id ?? '').trim();
  if (motor) trace.motor_audit_id = motor;
  const exec = String(o.execution_id ?? '').trim();
  if (exec) trace.execution_id = exec;
  const rid = String(o.request_id ?? '').trim();
  if (rid) trace.request_id = rid;
  const webFetchRaw = o.web_fetch;
  if (webFetchRaw && typeof webFetchRaw === 'object' && !Array.isArray(webFetchRaw)) {
    const wf = webFetchRaw as Record<string, unknown>;
    const wfUrl = String(wf.url ?? '').trim();
    const wfHost = String(wf.host ?? '').trim();
    if (wfUrl && wfHost) {
      trace.web_fetch = {
        url: wfUrl,
        host: wfHost,
        allowed: wf.allowed === true,
        chars: Number.isFinite(Number(wf.chars)) ? Math.max(0, Math.round(Number(wf.chars))) : 0,
      };
    }
  }
  trace.pipeline = (Array.isArray(o.pipeline) ? o.pipeline : []).flatMap((entry): ReasoningTraceStep[] => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
    const rawStep = entry as Record<string, unknown>;
    const step = String(rawStep.step ?? '').trim();
    if (!step) return [];
    const parsed: ReasoningTraceStep = { step, ok: rawStep.ok === true };
    const detail = String(rawStep.detail ?? '').trim();
    if (detail) parsed.detail = detail;
    const ms = Number(rawStep.ms);
    if (Number.isFinite(ms) && ms >= 0) parsed.ms = Math.round(ms);
    const chars = Number(rawStep.chars);
    if (Number.isFinite(chars) && chars >= 0) parsed.chars = Math.round(chars);
    return [parsed];
  });
  trace.warnings = Array.isArray(o.warnings)
    ? o.warnings.map((warning) => String(warning).trim()).filter(Boolean)
    : [];
  const error = String(o.error ?? '').trim();
  trace.error = error || null;
  return trace;
}

/** Support-Kopierblock: nur IDs und inhaltsfreie Pipeline-Diagnose. */
export function buildReasoningTraceSupportCopy(trace: ReasoningTrace): string {
  const lines = ['ARKO Support-Referenz'];
  if (trace.request_id) lines.push(`request_id: ${trace.request_id}`);
  if (trace.execution_id) lines.push(`execution_id: ${trace.execution_id}`);
  lines.push(`intent: ${trace.intent}`);
  lines.push(`path: ${trace.path}`);
  if (trace.heuristic_reason) lines.push(`heuristic_reason: ${trace.heuristic_reason}`);
  if (trace.mode) lines.push(`mode: ${trace.mode}`);
  if (trace.confidence != null) lines.push(`confidence: ${trace.confidence}`);
  if (trace.workflow_step) lines.push(`workflow_step: ${trace.workflow_step}`);
  if ((trace.sources ?? []).length > 0) {
    lines.push(`sources: ${(trace.sources ?? []).map((s) => s.norm_id).join(',')}`);
  }
  if (trace.web_fetch) {
    lines.push(`web_fetch: ${trace.web_fetch.host} chars=${trace.web_fetch.chars}`);
  }
  if ((trace.alternatives ?? []).length > 0) {
    lines.push(`alternatives: ${(trace.alternatives ?? []).join(',')}`);
  }
  if (trace.pipeline.length > 0) {
    lines.push(
      `pipeline: ${trace.pipeline
        .map((step) => `${step.step}=${step.ok ? 'ok' : 'not_ok'}${step.detail ? `(${step.detail})` : ''}${step.ms != null ? `:${step.ms}ms` : ''}`)
        .join(' > ')}`,
    );
  }
  if (trace.warnings.length > 0) lines.push(`warnings: ${trace.warnings.join(',')}`);
  if (trace.error) lines.push(`error: ${trace.error}`);
  return lines.join('\n');
}

export function hasReasoningTraceData(trace: ReasoningTrace | null | undefined): boolean {
  return Boolean(
    trace && (trace.intent || trace.path || trace.workflow_step || trace.heuristic_reason),
  );
}

/** Antwort-Pfad-Kategorie — dezente Bot-Bubble-Akzente (AI-Act: sichtbare Route, nicht nur Farbe). */
export type AnswerPathCategory =
  | 'orientierung'
  | 'lexcore'
  | 'judicature'
  | 'dokument'
  | 'schreiben'
  | 'frist';

export interface AnswerPathStyle {
  category: AnswerPathCategory;
  /** CSS-Farbe für linken Rand (2–3 px), ohne Glow. */
  borderColor: string;
  /** Barrierefreies Text-Label — immer neben/zu der Farbe anzeigen. */
  label: string;
  /** Textfarbe für das Label (Theme-kompatibel, CURSOR_PORTAL dark). */
  labelColor: string;
}

/**
 * AI-Act-Transparenz (Labor): `intent` + `path` aus `reasoning_trace` werden als
 * lesbares Pfad-Label + dezenter Rand an der Bot-Antwort gespiegelt — gleiche
 * Semantik wie ArkoReasoningTraceCard (Route), ohne Roh-CoT.
 */
const ANSWER_PATH_STYLES: Record<AnswerPathCategory, AnswerPathStyle> = {
  orientierung: {
    category: 'orientierung',
    borderColor: 'rgba(148, 163, 184, 0.42)',
    label: 'Orientierung',
    labelColor: 'rgba(148, 163, 184, 0.88)',
  },
  lexcore: {
    category: 'lexcore',
    borderColor: 'rgba(96, 165, 250, 0.48)',
    label: '[LEX]',
    labelColor: 'rgba(147, 197, 253, 0.92)',
  },
  judicature: {
    category: 'judicature',
    borderColor: 'rgba(167, 139, 250, 0.52)',
    label: '[JUD]',
    labelColor: 'rgba(196, 181, 253, 0.94)',
  },
  dokument: {
    category: 'dokument',
    borderColor: 'rgba(167, 139, 250, 0.45)',
    label: 'Dokument',
    labelColor: 'rgba(196, 181, 253, 0.9)',
  },
  schreiben: {
    category: 'schreiben',
    borderColor: 'rgba(251, 191, 36, 0.42)',
    label: 'Schreiben',
    labelColor: 'rgba(253, 224, 71, 0.88)',
  },
  frist: {
    category: 'frist',
    borderColor: 'rgba(45, 212, 191, 0.42)',
    label: 'Frist',
    labelColor: 'rgba(94, 234, 212, 0.88)',
  },
};

const FRIST_PATH_RE =
  /\b(frist_vorschlag|frist_motor|frist_probe|frist_heuristic)\b/i;
const SCHREIBEN_PATH_RE =
  /\b(schreiben_erstellen|schreiben_create|schreiben_draft|vertrag_erstellen|vertrag_generator|contract_create|schreiben_create)\b/i;
const LEXCORE_PATH_RE =
  /\b(rechtsfrage|lexcore|section_or_law_match|paragraph_wish_topic)\b/i;
const JUDICATURE_PATH_RE =
  /\b(judicature|judikatur|ogh|premium_entitlement_gate)\b/i;
const DOCUMENT_PATH_RE =
  /\b(document_context|document_welcome|background_document_analysis|document_question|topic_with_document|summary_or_meta|override_summary|chip_override_summary)\b/i;

function routingKeysForStyle(path: string, intent: string): string {
  const p = normalizeRoutingKey(path).toLowerCase();
  const i = normalizeRoutingKey(intent).toLowerCase();
  return [p, i, `${i}_${p}`, `${p}_${i}`].filter(Boolean).join('|');
}

function warningsHintLexcore(warnings: string[]): boolean {
  return warnings.some((w) => /\b(lexcore|rechtsfrage|norm_id|sources?)\b/i.test(w));
}

/** Klassifikation intent/path → Antwort-Pfad-Stil (Priorität: Frist > Schreiben > JUD > LEX > Dokument > Orientierung). */
export function classifyAnswerPathCategory(
  path: string,
  intent: string,
  warnings: string[] = [],
  hasLexcoreSources = false,
): AnswerPathCategory {
  const keys = routingKeysForStyle(path, intent);
  if (FRIST_PATH_RE.test(keys)) return 'frist';
  if (SCHREIBEN_PATH_RE.test(keys)) return 'schreiben';
  if (JUDICATURE_PATH_RE.test(keys)) return 'judicature';
  if (LEXCORE_PATH_RE.test(keys) || hasLexcoreSources || warningsHintLexcore(warnings)) {
    return 'lexcore';
  }
  if (DOCUMENT_PATH_RE.test(keys)) return 'dokument';
  return 'orientierung';
}

export function getAnswerPathStyle(
  path: string,
  intent: string,
  warnings: string[] = [],
  hasLexcoreSources = false,
): AnswerPathStyle {
  const category = classifyAnswerPathCategory(path, intent, warnings, hasLexcoreSources);
  return ANSWER_PATH_STYLES[category];
}

export function getAnswerPathStyleFromTrace(trace: ReasoningTrace | null | undefined): AnswerPathStyle | null {
  if (!trace || !hasReasoningTraceData(trace)) return null;
  const hasLexcoreSources = (trace.sources?.length ?? 0) > 0;
  return getAnswerPathStyle(trace.path, trace.intent, trace.warnings, hasLexcoreSources);
}
